import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { isOidcConfigured } from '../../lib/auth';

export type UserRole = 'admin' | 'ship-request';

/**
 * Hard-coded super-admins — always have 'admin' role regardless of DB state.
 * Used as a bootstrap so the panel is always accessible to core IT staff.
 */
export const SUPER_ADMIN_EMAILS = [
  'ccnanas@starmicronics.com',
  'ctrilone@starmicronics.com',
];

/** Resolves the current user's role: checks super-admins first, then fetches from DB. */
export function useUserRole(): { role: UserRole | null; loading: boolean } {
  const oidcEnabled = isOidcConfigured();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = oidcEnabled ? useAuth() : null;
  // Dev/preview bypass (no AuthProvider mounted): grant admin, matching AdminPage's fallback
  const [role, setRole] = useState<UserRole | null>(oidcEnabled ? null : 'admin');
  const [loading, setLoading] = useState(oidcEnabled);

  const email = (auth?.user?.profile?.email as string | undefined)?.toLowerCase();

  useEffect(() => {
    if (!auth?.isAuthenticated || !email) {
      setLoading(false);
      return;
    }
    if (SUPER_ADMIN_EMAILS.some((e) => e.toLowerCase() === email)) {
      setRole('admin');
      setLoading(false);
      return;
    }
    fetch('/api/ship-access')
      .then((r) => r.json())
      .then((data: { users?: { email: string; role: string }[] }) => {
        const found = data.users?.find((u) => u.email.toLowerCase() === email);
        setRole((found?.role as UserRole) ?? null);
      })
      .catch(() => setRole(null))
      .finally(() => setLoading(false));
  }, [auth?.isAuthenticated, email]);

  return { role, loading };
}

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  if (!isOidcConfigured()) {
    return <>{children}</>;
  }
  return <AdminGate>{children}</AdminGate>;
}

function AdminGate({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const { role, loading: roleLoading } = useUserRole();

  if (auth.isLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!role) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center px-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
        <p className="text-gray-500 text-sm">
          You don't have permission to view this page. Contact IT if you think this is a mistake.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
